import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import { GetMainSidebarInfo } from '../useChannels'
import type { MainSidebarChannel } from '../useChannels'

/**
 * GraphQL mutation for reordering channels within an account
 */
const OrderChannelForAccountMutation = graphql(/* GraphQL */ `
  mutation OrderChannelForAccount($input: OrderChannelForAccountInput!) {
    orderChannelForAccount(input: $input) {
      ... on EmptySuccess {
        __typename
      }
      ... on MutationError {
        __typename
        message
      }
    }
  }
`)

interface OrderChannelParams {
  organizationId: string
  channelId: string
  dragIndex: number
  hoverIndex: number
  channels: MainSidebarChannel[]
}

/**
 * Custom hook for managing channel reordering mutations in the MainSidebar.
 * Handles optimistic updates, error recovery, and cache management.
 *
 * @returns {Function} A function to reorder channels with optimistic updates and error handling
 *
 * @example
 * ```tsx
 * const { orderChannel } = useOrderChannelMutation()
 *
 * await orderChannel({
 *   organizationId: 'org-123',
 *   channelId: 'ch-456',
 *   dragIndex: 2,
 *   hoverIndex: 0,
 *   channels: allChannels,
 * })
 * ```
 */
export function useOrderChannelMutation(): {
  orderChannel: (params: OrderChannelParams) => Promise<void>
} {
  const [orderChannelMutation] = useTypedMutation(
    OrderChannelForAccountMutation,
    (data) => data.orderChannelForAccount,
    {
      successTypename: 'EmptySuccess',
      refetchQueries: [GetMainSidebarInfo],
      awaitRefetchQueries: true,
    },
  )

  const orderChannel = async ({
    organizationId,
    channelId,
    dragIndex,
    hoverIndex,
    channels,
  }: OrderChannelParams): Promise<void> => {
    if (!organizationId || channels.length === 0) return

    // Get the channel being moved
    const draggedChannel = channels[dragIndex]
    if (!draggedChannel?.id) return

    // Create a new array with the updated order for optimistic update
    const newChannels = [...channels]
    newChannels.splice(dragIndex, 1)
    newChannels.splice(hoverIndex, 0, draggedChannel)

    // Determine the correct "afterChannelId" based on move direction
    // When moving down, position after the channel at the target index
    // When moving up, position after the channel before the target index
    const isMovingDown = dragIndex < hoverIndex
    const targetIndex = isMovingDown ? hoverIndex : hoverIndex - 1
    const afterChannel = targetIndex >= 0 ? channels[targetIndex] : null

    try {
      const { success, error } = await orderChannelMutation({
        variables: {
          input: {
            organizationId,
            channelId,
            afterChannelId: afterChannel?.id,
          },
        },
        optimisticResponse: {
          orderChannelForAccount: {
            __typename: 'EmptySuccess',
          },
        },
        update: (cache) => {
          // Read existing data from cache
          const existingData = cache.readQuery({
            query: GetMainSidebarInfo,
          })

          if (!existingData?.account?.currentOrganization) return

          // Write optimistically updated data to cache
          cache.writeQuery({
            query: GetMainSidebarInfo,
            data: {
              account: {
                ...existingData.account,
                currentOrganization: {
                  ...existingData.account.currentOrganization,
                  channels: newChannels,
                },
              },
            },
          })
        },
      })

      if (!success) {
        // Apollo automatically rolls back optimistic updates on error
        // The refetchQueries will fetch fresh data from the server
        toast.error(`Failed to reorder channels: ${error.message}`)
      }
    } catch (error) {
      // Apollo automatically rolls back optimistic updates on exception
      // The refetchQueries will fetch fresh data from the server
      if (error instanceof Error) {
        toast.error(`Failed to reorder channels: ${error.message}`)
      } else {
        toast.error('Failed to reorder channels')
      }
    }
  }

  return { orderChannel }
}
